import React, { useMemo, useEffect, useState } from "react";
import {
  Table,
  Flex,
  Tooltip,
  IconButton,
  Icon,
  useColorModeValue,
  TableContainer,
  HStack,
  useDisclosure,
} from "@chakra-ui/react";
import { formatDate } from "lib/utils";
import Card from "components/Card/Card.js";
import { useGetPaginatedEvents, useDeleteEvent } from "queries/useEvent";
import { useForm } from "react-hook-form";
import useDataTable from "hooks/useDataTable";
import { dynamicSort } from "utils/helpers";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import Header from "components/ui/table/Header";
import Body from "components/ui/table/Body";
import Pagination from "components/ui/table/Pagination";
import { Search } from "components/ui/table/Search";
import { FaPlus } from "react-icons/fa6";
import { CreatedAt } from "components/CellElements";
import { Basic } from "components/CellElements";
import { Title } from "components/CellElements";
import { RiEdit2Fill } from "react-icons/ri";
import { FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

function Events() {
  const { formMethods, table, deleteModal, navigate, bgButton } = useEvents();

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <HStack spacing={4} mb={4} mx="6">
          <Search methods={formMethods} />
          <Tooltip label="Add New Event" placement="top">
            <IconButton
              aria-label="add new event"
              icon={<Icon as={FaPlus} boxSize={4} />}
              colorScheme="primary"
              bg={bgButton}
              onClick={() => navigate("/events/create")}
            />
          </Tooltip>
        </HStack>
        <TableContainer>
          <Table {...table.getTableProps()}>
            <Header {...table.header} />
            <Body {...table.body} />
          </Table>
        </TableContainer>
        <Pagination mt="10" {...table.pagination} />
        <DeleteConfirmationModal {...deleteModal} />
      </Card>
    </Flex>
  );
}

export default Events;

const useEvents = () => {
  const navigate = useNavigate();
  const deleteModal = useDisclosure();
  const formMethods = useForm({
    defaultValues: {
      search: localStorage.getItem("eventsSearch") || "",
    },
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(() => {
    const savedLimit = localStorage.getItem("eventsLimit");
    return savedLimit ? parseInt(savedLimit, 10) : 50;
  });
  const [currentSort, setCurrentSort] = useState([["createdAt", "DESC"]]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const watchSearch = formMethods?.watch("search");

  const getEvents = useGetPaginatedEvents({
    page: currentPage,
    limit,
    search: watchSearch,
    order: currentSort,
  });
  const deleteEvent = useDeleteEvent({
    onSuccess: () => deleteModal.onClose(),
  });

  const onSort = dynamicSort(["createdAt"]);
  const data = useMemo(() => getEvents.data?.data?.events || [], [
    JSON.stringify(getEvents.data),
  ]);

  const onDelete = (event) => {
    setSelectedContactId(event?.id);
    deleteModal.onOpen();
  };

  const onEdit = (event) => navigate(`/events/view/${event?.id}`);

  const columns = useMemo(
    () => [
      {
        Header: "Title",
        id: "title",
        accessor: (event) => event.title,
        Cell: ({ value }) => <Title title={`${value}`} />,
        sortType: ({ original: a }, { original: b }) => onSort(a, b),
      },
      {
        Header: "Dates",
        accessor: (event) => event,
        Cell: ({ value: { startDate, endDate } }) => (
          <Basic
            text={`${formatDate(startDate, "MMM DD")} to ${formatDate(
              endDate,
              "MMM DD, YYYY"
            )}`}
          />
        ),
        sortType: ({ original: a }, { original: b }) => onSort(a, b),
      },
      {
        Header: "Location",
        accessor: (event) => event,
        Cell: ({ value: { city, state } }) => {
          const location =
            city || state
              ? `${city || ""}${city && state ? ", " : ""}${state || ""}`
              : "";
          return <Basic text={location} />;
        },
        sortType: ({ original: a }, { original: b }) => onSort(a, b),
      },
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ value }) => <CreatedAt date={value} />,
      },
      {
        accessor: "id",
        disableSortBy: true,
        Cell: ({ row }) => (
          <HStack spacing={3}>
            <Tooltip label="View" placement="top">
              <IconButton
                aria-label="view event"
                icon={<Icon as={RiEdit2Fill} boxSize={5} />}
                onClick={() => onEdit(row.original)}
              />
            </Tooltip>
            <Tooltip label="Delete" placement="top">
              <IconButton
                aria-label="delete event"
                icon={<Icon as={FaTrash} boxSize={4} />}
                colorScheme="red"
                onClick={() => onDelete(row.original)}
              />
            </Tooltip>
          </HStack>
        ),
      },
    ],
    []
  );

  const sortTable = useMemo(() => {
    return currentSort.map(([column, direction]) => {
      let id =
        column?.replace("Dates", "startDate")?.replace("Location", "city") ||
        "createdAt";
      let desc = direction === "ASC" ? false : true;
      return { id, desc };
    });
  }, [currentSort]);

  const pageCount = useMemo(() => getEvents.data?.data?.totalPages || 0, [
    getEvents.data,
  ]);

  const table = useDataTable({
    desktopHiddenColumns: [],
    mobileHiddenColumns: [],
    data,
    columns,
    initialState: {
      pageCount,
      pageIndex: currentPage - 1,
      pageSize: limit,
      sortBy: sortTable,
    },
    setter: { setCurrentPage, setLimit },
    isLoading: getEvents.isLoading,
  });

  useEffect(() => {
    getEvents.refetch({
      page: table.pagination.pageIndex,
      limit: table.pagination.pageSize,
      order: currentSort,
      search: watchSearch,
    });

    localStorage.setItem("eventsPage", table.pagination.pageIndex.toString());
    localStorage.setItem("eventsLimit", table.pagination.pageSize.toString());

    if (watchSearch !== undefined) {
      localStorage.setItem("eventsSearch", watchSearch || "");
    }
  }, [
    table.pagination.pageIndex,
    table.pagination.pageSize,
    watchSearch,
    JSON.stringify(table.sort?.sortBy),
  ]);

  useEffect(() => {
    let sort = table.sort?.sortBy?.map((s) => {
      let id =
        s.id?.replace("Dates", "startDate")?.replace("Location", "city") ||
        "createdAt";
      let desc = s.desc == false ? "ASC" : "DESC";
      return [id, desc];
    });

    setCurrentSort(sort);
  }, [JSON.stringify(table.sort?.sortBy)]);

  const bgButton = useColorModeValue(
    "linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)",
    "gray.800"
  );

  return {
    bgButton,
    formMethods,
    table,
    navigate,
    deleteModal: {
      ...deleteModal,
      onConfirmDelete: () => deleteEvent.mutate(selectedContactId),
      isLoading: deleteEvent.isLoading,
      title: "Are you sure you want to delete this event?",
    },
  };
};
